// src/pages/MatchProtocolPage.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { getToken } from "@/api/client";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Target, AlertTriangle, Play, Save, ArrowRight, ArrowLeft, Crosshair, Rows3, Table2, CalendarDays, Clock3, BarChart3, Flag, Users, Repeat2, RotateCcw, CheckCircle2 } from "lucide-react";
import HeatDialog from "@/components/HeatDialog";
import NominationDialog from "@/components/NominationDialog";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { ConfirmButton } from "@/components/ConfirmButton";
import HeatsCarousel from "@/components/HeatsCarousel";
import MatchStatsDialog from "@/components/MatchStatsDialog";
import TeamLogo from "@/components/TeamLogo";
import GuestRiderBadge from "@/components/GuestRiderBadge";
import HeatProgramMatrix from "@/components/protocol/HeatProgramMatrix";
import { useProtocolShell } from "@/contexts/ProtocolShellContext";
import { useAccountPreferences, useTutorialStatus, useUpdateAccountPreferences } from "@/queries/account";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";

import PageTutorial from "@/components/tutorial/PageTutorial";
import { formatSvDateTime } from "@/utils/date";
import { resolveSeason } from "@/lib/season";
import { isGuestRiderForTeam } from "@/lib/guestRiders";
import { getNativePlatform } from "@/lib/runtime";
import { NativeProtocolSkeleton } from "@/components/skeletons/NativeLoadingSkeletons";


// --- TanStack Query hooks (bygger på dina api-funktioner under huven)
import { useMatch, useConfirmMatch, useRefreshMatchLineup, useRestartMatchLineup, useResetMatchToInitialLineup, useSaveHeat, useUpdateManualMatchLineup } from "@/queries/matches";
import { useTeamRiders } from "@/queries/teams";


import AdSlot from "@/components/AdSlot";


// Hjälpfunktion som verifierar att alla heat är sparade på ett komplett sätt
function isMatchComplete(match) {
  if (!match?.heats || match.heats.length < 15) return false;
  return match.heats.every(
    (h) => h?.status === "completed" && (h?.results?.length ?? 0) >= 4
  );
}

// Skelett för sidan som visas medan matchdata laddas
function MatchProtocolSkeleton({ native = false }) {
  if (native) return <NativeProtocolSkeleton />;

  return (
    <div className="space-y-2">
      <Card className="border-secondary bg-card">
        <CardContent className="space-y-2 px-4 pb-3 pt-3 sm:px-5 sm:pb-4">
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 text-center sm:gap-4">
            <div className="flex flex-col items-center">
              <Skeleton className="mb-1 h-8 w-8 rounded-full" />
              <Skeleton className="mb-1 h-3 w-20" />
              <div className="mt-1 flex justify-center gap-1">
                <Skeleton className="h-3 w-3 rounded-full" />
                <Skeleton className="h-3 w-3 rounded-full" />
              </div>
            </div>
            <div className="flex flex-col items-center justify-center gap-2 self-center">
              <div className="flex items-center justify-center gap-2">
                <Skeleton className="h-8 w-10" />
                <Skeleton className="h-3 w-3" />
                <Skeleton className="h-8 w-10" />
              </div>
              <div className="flex items-center gap-3">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-3 w-12" />
              </div>
            </div>
            <div className="flex flex-col items-center">
              <Skeleton className="mb-1 h-8 w-8 rounded-full" />
              <Skeleton className="mb-1 h-3 w-20" />
              <div className="mt-1 flex justify-center gap-1">
                <Skeleton className="h-3 w-3 rounded-full" />
                <Skeleton className="h-3 w-3 rounded-full" />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between gap-3">
            <Skeleton className="h-3 w-28" />
            <Skeleton className="h-3 w-24" />
          </div>
        </CardContent>
      </Card>

      <div className="mx-auto h-[50px] w-full max-w-[320px]" aria-hidden="true" />

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-10 w-[146px] rounded-full" />
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-4 pt-0 sm:px-5">
          <Skeleton className="h-72 w-full rounded-xl" />
        </CardContent>
      </Card>
    </div>
  );
}

const MANUAL_LINEUP_SLOTS = [1, 2, 3, 4, 5, 6, 7];

function emptyManualLineupState() {
  return Object.fromEntries(MANUAL_LINEUP_SLOTS.map((slot) => [String(slot), ""]));
}

function buildManualLineupState(entries = []) {
  const base = emptyManualLineupState();
  for (const item of Array.isArray(entries) ? entries : []) {
    const slot = String(item?.lineup_no ?? "");
    if (slot in base) base[slot] = String(item?.rider_id ?? item?.id ?? "");
  }
  return base;
}

function manualLineupStateEquals(a, b) {
  return MANUAL_LINEUP_SLOTS.every((slot) => String(a?.[String(slot)] || "") === String(b?.[String(slot)] || ""));
}

function findManualLineupRiderSlot(state, riderId, currentSlot) {
  const normalizedRiderId = String(riderId || "");
  const normalizedSlot = String(currentSlot || "");
  return MANUAL_LINEUP_SLOTS.find((slot) => {
    const slotKey = String(slot);
    if (slotKey === normalizedSlot) return false;
    return String(state?.[slotKey] || "") === normalizedRiderId;
  }) ?? null;
}

function buildManualLineupPayload(state, options) {
  const optionMap = new Map((options || []).map((item) => [String(item.id), item]));
  return MANUAL_LINEUP_SLOTS.map((slot) => {
    const riderId = String(state?.[String(slot)] || "");
    const rider = optionMap.get(riderId);
    if (!riderId || !rider) return null;
    return {
      id: riderId,
      rider_id: riderId,
      name: rider.name,
      rider_name: rider.name,
      lineup_no: slot,
      is_reserve: slot >= 6,
      team_id: rider.team_id || "",
      club_team_id: rider.club_team_id || rider.team_id || "",
      guest_active: Boolean(rider.guest_active),
      guest_for_team_id: rider.guest_for_team_id || "",
      guest_note: rider.guest_note || "",
      is_guest_for_team: Boolean(rider.is_guest_for_team),
    };
  }).filter(Boolean);
}

function buildManualLineupDraftChanges(currentState, baselineState, options = []) {
  const optionMap = new Map((options || []).map((item) => [String(item.id), item]));
  const initialSlotByRider = new Map();
  const changes = {};
  const swapSlotsHandled = new Set();

  const getRiderName = (riderId) => optionMap.get(String(riderId || ""))?.name || "Okänd förare";

  MANUAL_LINEUP_SLOTS.forEach((slot) => {
    const riderId = String(baselineState?.[String(slot)] || "");
    if (riderId) initialSlotByRider.set(riderId, slot);
  });

  MANUAL_LINEUP_SLOTS.forEach((slot) => {
    const slotKey = String(slot);
    if (swapSlotsHandled.has(slotKey)) return;

    const initialRiderId = String(baselineState?.[slotKey] || "");
    const currentRiderId = String(currentState?.[slotKey] || "");
    if (!initialRiderId || !currentRiderId || initialRiderId === currentRiderId) return;

    const currentRiderInitialSlot = initialSlotByRider.get(currentRiderId);
    if (
      currentRiderInitialSlot &&
      currentRiderInitialSlot !== slot &&
      String(currentState?.[String(currentRiderInitialSlot)] || "") === initialRiderId
    ) {
      const counterpartKey = String(currentRiderInitialSlot);
      changes[slotKey] = {
        type: "swap",
        withSlot: currentRiderInitialSlot,
        currentRiderName: getRiderName(currentRiderId),
        displacedRiderName: getRiderName(initialRiderId),
      };
      changes[counterpartKey] = {
        type: "swap",
        withSlot: slot,
        currentRiderName: getRiderName(String(currentState?.[counterpartKey] || "")),
        displacedRiderName: getRiderName(String(baselineState?.[counterpartKey] || "")),
      };
      swapSlotsHandled.add(slotKey);
      swapSlotsHandled.add(counterpartKey);
      return;
    }

    changes[slotKey] = {
      type: "replace",
      inRiderId: currentRiderId,
      inRiderName: getRiderName(currentRiderId),
      outRiderId: initialRiderId,
      outRiderName: getRiderName(initialRiderId),
      sourceSlot: currentRiderInitialSlot || null,
    };
  });

  return changes;
}

function getManualLineupTriggerContent(riderId, optionMap, draftChange, teamId = "") {
  const rider = optionMap.get(String(riderId || ""));
  const riderName = rider?.name || "";
  if (!riderName) {
    return <span className="text-muted-foreground">Välj förare</span>;
  }

  return (
    <div className="flex min-w-0 items-center justify-between gap-3">
      <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
        <span className="truncate">{riderName}</span>
        {isGuestRiderForTeam(rider, teamId) ? <GuestRiderBadge className="shrink-0" /> : null}
      </div>
      {draftChange ? (
        draftChange.type === "swap" ? (
          <span className="ml-1 inline-flex shrink-0 items-center rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-800 dark:text-amber-300">
            <Repeat2 className="h-3.5 w-3.5" />
          </span>
        ) : (
          <span className="ml-1 inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-800 dark:text-emerald-300">
            <ArrowRight className="h-3.5 w-3.5" />
            In
          </span>
        )
      ) : null}
    </div>
  );
}

function ProtocolScoreboard({
  match,
  homeScore,
  awayScore,
  completedHeats,
  formatMatchDate,
  formatMatchTime,
  showFooter = true,
  onEditLineup = null,
}) {
  return (
    <Card id="matchProtocolOverview" className="bg-card border-secondary">
      <CardContent className="space-y-2 px-4 pb-3 pt-3 sm:px-5 sm:pb-4">
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 text-center sm:gap-4">
          <div className="flex flex-col items-center">
            <TeamLogo
              teamName={match.home_team}
              size={32}
              className="mb-1 h-8 w-8 shrink-0 object-contain"
            />
            <div className="mb-1 text-[11px] font-medium leading-tight text-gray-600 sm:text-xs">
              {match.home_team}
            </div>
            <div className="mt-1 flex justify-center space-x-1">
              <div className="h-3 w-3 rounded-full bg-red-600"></div>
              <div className="h-3 w-3 rounded-full bg-blue-600"></div>
            </div>
          </div>
          <div className="flex flex-col items-center justify-center gap-1 self-center">
            <div className="flex items-center justify-center gap-2">
              <div className="text-[28px] font-bold leading-none text-red-600 sm:text-[30px]">
                {homeScore}
              </div>
              <div className="text-sm text-gray-400 sm:text-base">-</div>
              <div className="text-[28px] font-bold leading-none text-yellow-600 sm:text-[30px]">
                {awayScore}
              </div>
            </div>
            <div className="flex items-center gap-3 text-[11px] text-muted-foreground sm:text-xs">
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                <CalendarDays className="h-3.5 w-3.5" />
                {formatMatchDate(match.date)}
              </span>
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                <Clock3 className="h-3.5 w-3.5" />
                {formatMatchTime(match.date)}
              </span>
            </div>
          </div>
          <div className="flex flex-col items-center">
            <TeamLogo
              teamName={match.away_team}
              size={32}
              className="mb-1 h-8 w-8 shrink-0 object-contain"
            />
            <div className="mb-1 text-[11px] font-medium leading-tight text-gray-600 sm:text-xs">
              {match.away_team}
            </div>
            <div className="mt-1 flex justify-center space-x-1">
              <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
              <div className="h-3 w-3 rounded-full border border-gray-300 bg-white"></div>
            </div>
          </div>
        </div>
        {showFooter ? (
          <div className="native-v3-running-statusbar flex items-center justify-between gap-3 text-[11px] text-muted-foreground sm:text-xs">
            <div className="native-v3-running-heat-status inline-flex items-center gap-1.5">
              <Flag className="h-3.5 w-3.5" />
              Heat {completedHeats}/15
            </div>
            <div className="flex items-center gap-3">
              {onEditLineup ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onEditLineup}
                  className="h-7 gap-1.5 px-2 text-[11px] sm:h-8 sm:text-xs"
                >
                  <Users className="h-3.5 w-3.5" />
                  Ändra laguppställning
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}


export default function MatchProtocolPage({
  matchId: matchIdProp = null,
  embedded = false,
  onExit = null,
  embeddedFooterId = null,
}) {
  const params = useParams();
  const id = matchIdProp ?? params?.id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { resumeToken, lineupAlertFocusToken, lineupAlertFocusMatchId } = useProtocolShell();
  const isNativeAndroid = getNativePlatform() === "android";

  // Dialog/side state
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedHeat, setSelectedHeat] = useState(null);
  const [nomOpen, setNomOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const [currentHeatIndex, setCurrentHeatIndex] = useState(0);
  const [currentSlideMeta, setCurrentSlideMeta] = useState(null);
  const [focusTarget, setFocusTarget] = useState(null);
  const [protocolViewMode, setProtocolViewMode] = useState("cards");
  const [tableIsFocusedHeatInView, setTableIsFocusedHeatInView] = useState(false);
  const [tableFocusTarget, setTableFocusTarget] = useState(null);
  const [showProtocolViewPrompt, setShowProtocolViewPrompt] = useState(false);
  const [lineupAlertHighlighted, setLineupAlertHighlighted] = useState(false);
  const [manualLineupOpen, setManualLineupOpen] = useState(false);
  const [manualLineupTeam, setManualLineupTeam] = useState("home");
  const [pendingManualSwap, setPendingManualSwap] = useState(null);
  const [pendingManualLineupReset, setPendingManualLineupReset] = useState(null);
  const [pendingManualLineupDiscard, setPendingManualLineupDiscard] = useState(false);
  const [completeProtocolPromptOpen, setCompleteProtocolPromptOpen] = useState(false);
  const [embeddedFooterTarget, setEmbeddedFooterTarget] = useState(null);
  const [manualHomeState, setManualHomeState] = useState(() => emptyManualLineupState());
  const [manualAwayState, setManualAwayState] = useState(() => emptyManualLineupState());
  const [matchProtocolTutorialState, setMatchProtocolTutorialState] = useState(null);
  const prevCompletedHeatsRef = React.useRef(0);
  const defaultViewAppliedRef = React.useRef(false);
  const tableInitialPositionedRef = React.useRef(false);
  const pageRootRef = React.useRef(null);
  const lineupAlertRef = React.useRef(null);
  const completeProtocolPromptTimeoutRef = React.useRef(null);
  const { data: preferenceData } = useAccountPreferences();
  const { data: tutorialData, isLoading: tutorialLoading } = useTutorialStatus();
  const updatePreferences = useUpdateAccountPreferences();
  const autoScrollNextHeatEnabled =
    preferenceData?.preferences?.auto_scroll_next_heat !== false;

  useEffect(() => {
    if (!embedded || !embeddedFooterId) {
      setEmbeddedFooterTarget(null);
      return undefined;
    }

    const target = document.getElementById(embeddedFooterId);
    setEmbeddedFooterTarget(target);
    return () => setEmbeddedFooterTarget(null);
  }, [embedded, embeddedFooterId, id]);

  // Riders (hämtas via cache:ad query per lag)

  // Hämta match-detalj via TanStack Query
  const {
    data: match,
    isLoading: loading,
    error,
    refetch, // vi styr när vi vill hämta om (efter sparningar etc.)
  } = useMatch(id, {
    enabled: !!id && id !== "preview",
    staleTime: Infinity, // redigera lugnt utan auto-refetch; vi kör refetch() explicit
  });




  const homeTeamId = match?.home_team_id;
  const awayTeamId = match?.away_team_id;
  const rosterSeason = useMemo(() => {
    const matchDateYear = match?.date ? new Date(match.date).getFullYear() : undefined;
    return resolveSeason(match?.season, matchDateYear);
  }, [match?.date, match?.season]);
  const { data: homeRidersData } = useTeamRiders(homeTeamId, {
    enabled: Boolean(homeTeamId),
    season: rosterSeason,
    active: true,
    includeGuests: true,
    officialMatchId: match?.official_match_id,
  });
  const { data: awayRidersData } = useTeamRiders(awayTeamId, {
    enabled: Boolean(awayTeamId),
    season: rosterSeason,
    active: true,
    includeGuests: true,
    officialMatchId: match?.official_match_id,
  });
  const ridersByTeam = useMemo(() => {
    const fromLineups = (entries = []) =>
      entries.map((entry) => {
        const lineupNo = Number(entry?.lineup_no);
        return {
          id: String(entry?.rider_id || entry?.id || ""),
          name: entry?.name || entry?.rider_name || "",
          lineup_no: Number.isFinite(lineupNo) ? lineupNo : null,
          is_reserve: Number.isFinite(lineupNo) ? lineupNo >= 6 : false,
          team_id: entry?.team_id || "",
          club_team_id: entry?.club_team_id || entry?.team_id || "",
          guest_active: Boolean(entry?.guest_active),
          guest_for_team_id: entry?.guest_for_team_id || "",
          guest_note: entry?.guest_note || "",
          is_guest_for_team: Boolean(entry?.is_guest_for_team),
        };
      }).filter((r) => r.id);

    const toOpts = (xs = []) =>
      xs.map((r) => ({
        id: String(r.id),
        name: r.name,
        lineup_no: r.lineup_no,
        is_reserve: Boolean(r.is_reserve),
        team_id: r.team_id || "",
        club_team_id: r.club_team_id || r.team_id || "",
        guest_active: Boolean(r.guest_active),
        guest_for_team_id: r.guest_for_team_id || "",
        guest_note: r.guest_note || "",
        is_guest_for_team: Boolean(r.is_guest_for_team),
      }));

    const homeLineup = fromLineups(match?.lineups?.home || []);
    const awayLineup = fromLineups(match?.lineups?.away || []);
    const homeInitialLineup = fromLineups(match?.initial_lineups?.home || []);
    const awayInitialLineup = fromLineups(match?.initial_lineups?.away || []);
    const homeSourceLineup = fromLineups(match?.source_lineups?.home || []);
    const awaySourceLineup = fromLineups(match?.source_lineups?.away || []);
    const mergeRoster = (rosterEntries = [], lineupGroups = []) => {
      const merged = new Map();
      for (const rider of rosterEntries) {
        if (!rider?.id) continue;
        merged.set(String(rider.id), rider);
      }
      lineupGroups.forEach((lineupEntries, groupIndex) => {
        for (const rider of lineupEntries) {
          if (!rider?.id) continue;
          const key = String(rider.id);
          const existing = merged.get(key) || {};
          merged.set(key, {
            ...existing,
            ...rider,
            lineup_no: rider.lineup_no ?? existing.lineup_no ?? null,
            is_reserve: typeof rider.is_reserve === "boolean" ? rider.is_reserve : Boolean(existing.is_reserve),
            team_id: rider.team_id || existing.team_id || "",
            club_team_id: rider.club_team_id || existing.club_team_id || rider.team_id || existing.team_id || "",
            guest_active: Boolean(rider.guest_active),
            guest_for_team_id: rider.guest_for_team_id || "",
            guest_note: rider.guest_note || "",
            is_guest_for_team: Boolean(rider.is_guest_for_team),
          });
        }
      });
      return Array.from(merged.values()).sort((a, b) => {
        const aReserve = a?.is_reserve ? 1 : 0;
        const bReserve = b?.is_reserve ? 1 : 0;
        if (aReserve !== bReserve) return aReserve - bReserve;
        const aNo = Number.isFinite(Number(a?.lineup_no)) ? Number(a.lineup_no) : 99;
        const bNo = Number.isFinite(Number(b?.lineup_no)) ? Number(b.lineup_no) : 99;
        if (aNo !== bNo) return aNo - bNo;
        return String(a?.name || "").localeCompare(String(b?.name || ""), "sv-SE");
      });
    };

    return {
      home: mergeRoster([
        ...toOpts(homeRidersData?.mains || []),
        ...toOpts(homeRidersData?.reserves || []),
      ], [homeLineup, homeInitialLineup, homeSourceLineup]),
      away: mergeRoster([
        ...toOpts(awayRidersData?.mains || []),
        ...toOpts(awayRidersData?.reserves || []),
      ], [awayLineup, awayInitialLineup, awaySourceLineup]),
    };
  }, [awayRidersData, homeRidersData, match?.initial_lineups, match?.lineups, match?.source_lineups]);

  const manualHomeOptions = useMemo(() => ridersByTeam.home || [], [ridersByTeam.home]);
  const manualAwayOptions = useMemo(() => ridersByTeam.away || [], [ridersByTeam.away]);
  const manualHomeOptionMap = useMemo(
    () => new Map(manualHomeOptions.map((rider) => [String(rider.id), rider])),
    [manualHomeOptions]
  );
  const manualAwayOptionMap = useMemo(
    () => new Map(manualAwayOptions.map((rider) => [String(rider.id), rider])),
    [manualAwayOptions]
  );

  useEffect(() => {
    setManualHomeState(buildManualLineupState(match?.lineups?.home || []));
    setManualAwayState(buildManualLineupState(match?.lineups?.away || []));
  }, [match?.lineups]);

  const manualHomeInitialState = useMemo(
    () => buildManualLineupState(match?.lineups?.home || []),
    [match?.lineups?.home]
  );
  const manualAwayInitialState = useMemo(
    () => buildManualLineupState(match?.lineups?.away || []),
    [match?.lineups?.away]
  );
  const manualHomeBaselineState = useMemo(
    () => buildManualLineupState(match?.initial_lineups?.home || match?.lineups?.home || []),
    [match?.initial_lineups?.home, match?.lineups?.home]
  );
  const manualAwayBaselineState = useMemo(
    () => buildManualLineupState(match?.initial_lineups?.away || match?.lineups?.away || []),
    [match?.initial_lineups?.away, match?.lineups?.away]
  );

  const manualHomeDirty = !manualLineupStateEquals(manualHomeState, manualHomeInitialState);
  const manualAwayDirty = !manualLineupStateEquals(manualAwayState, manualAwayInitialState);
  const manualHomePersistedResettable = !manualLineupStateEquals(manualHomeInitialState, manualHomeBaselineState);
  const manualAwayPersistedResettable = !manualLineupStateEquals(manualAwayInitialState, manualAwayBaselineState);
  const manualHomeCanReset = manualHomeDirty || manualHomePersistedResettable;
  const manualAwayCanReset = manualAwayDirty || manualAwayPersistedResettable;
  const manualHomeDraftChanges = useMemo(
    () => buildManualLineupDraftChanges(manualHomeState, manualHomeInitialState, manualHomeOptions),
    [manualHomeInitialState, manualHomeOptions, manualHomeState]
  );
  const manualAwayDraftChanges = useMemo(
    () => buildManualLineupDraftChanges(manualAwayState, manualAwayInitialState, manualAwayOptions),
    [manualAwayInitialState, manualAwayOptions, manualAwayState]
  );

  const manualLineupDirty =
    manualHomeDirty || manualAwayDirty;

  const manualLineupComplete = useMemo(() => {
    const homeIds = MANUAL_LINEUP_SLOTS.map((slot) => String(manualHomeState[String(slot)] || ""));
    const awayIds = MANUAL_LINEUP_SLOTS.map((slot) => String(manualAwayState[String(slot)] || ""));
    const homeFilled = homeIds.every(Boolean);
    const awayFilled = awayIds.every(Boolean);
    const homeUnique = new Set(homeIds).size === homeIds.length;
    const awayUnique = new Set(awayIds).size === awayIds.length;
    return homeFilled && awayFilled && homeUnique && awayUnique;
  }, [manualAwayState, manualHomeState]);

  const handleManualLineupSelect = useCallback((team, slot, riderId) => {
    const normalizedSlot = String(slot);
    const normalizedRiderId = String(riderId || "");
    const state = team === "home" ? manualHomeState : manualAwayState;
    const setter = team === "home" ? setManualHomeState : setManualAwayState;
    const optionMap = team === "home" ? manualHomeOptionMap : manualAwayOptionMap;
    const occupiedSlot = findManualLineupRiderSlot(state, normalizedRiderId, normalizedSlot);

    if (!occupiedSlot) {
      setter((current) => ({
        ...current,
        [normalizedSlot]: normalizedRiderId,
      }));
      return;
    }

    const currentRiderId = String(state?.[normalizedSlot] || "");
    if (!currentRiderId || currentRiderId === normalizedRiderId) {
      setter((current) => ({
        ...current,
        [normalizedSlot]: normalizedRiderId,
      }));
      return;
    }

    const selectedRider = optionMap.get(normalizedRiderId);
    const currentRider = optionMap.get(currentRiderId);

    setPendingManualSwap({
      team,
      slot: normalizedSlot,
      targetSlot: String(occupiedSlot),
      selectedRiderId: normalizedRiderId,
      selectedRiderName: selectedRider?.name || "Vald förare",
      currentRiderId,
      currentRiderName: currentRider?.name || "Nuvarande förare",
    });
  }, [manualAwayOptionMap, manualAwayState, manualHomeOptionMap, manualHomeState]);

  const confirmManualSwap = useCallback(() => {
    if (!pendingManualSwap) return;
    const setter = pendingManualSwap.team === "home" ? setManualHomeState : setManualAwayState;
    setter((current) => {
      const next = { ...current };
      next[pendingManualSwap.slot] = pendingManualSwap.selectedRiderId;
      next[pendingManualSwap.targetSlot] = pendingManualSwap.currentRiderId;
      return next;
    });
    setPendingManualSwap(null);
  }, [pendingManualSwap]);

  const handleResetManualLineupTeam = useCallback((team) => {
    const persistedResettable = team === "home" ? manualHomePersistedResettable : manualAwayPersistedResettable;
    const baselineState = team === "home" ? manualHomeBaselineState : manualAwayBaselineState;

    if (!persistedResettable) {
      if (team === "home") {
        setManualHomeState(baselineState);
        return;
      }
      setManualAwayState(baselineState);
      return;
    }

    const hasProtocolProgress = Boolean(
      (match?.heats || []).some(
        (heat) => (heat?.results?.length || 0) > 0 || String(heat?.status || "upcoming") !== "upcoming"
      )
    );

    setPendingManualLineupReset({
      team,
      teamName: team === "home" ? (match?.home_team || "Hemmalag") : (match?.away_team || "Bortalag"),
      hasProtocolProgress,
    });
  }, [
    manualAwayBaselineState,
    manualAwayPersistedResettable,
    manualHomeBaselineState,
    manualHomePersistedResettable,
    match?.away_team,
    match?.heats,
    match?.home_team,
  ]);

  const closeManualLineupModal = useCallback(() => {
    setManualLineupOpen(false);
    setPendingManualSwap(null);
    setPendingManualLineupReset(null);
    setPendingManualLineupDiscard(false);
  }, []);

  const requestCloseManualLineupModal = useCallback(() => {
    if (manualLineupDirty) {
      setPendingManualLineupDiscard(true);
      return;
    }
    closeManualLineupModal();
  }, [closeManualLineupModal, manualLineupDirty]);

  // Auth/Access-fall
  useEffect(() => {
    if (!error) return;
    const status = error.status || error?.response?.status;
    if (status === 401 || status === 403) {
      if (embedded && typeof onExit === "function") {
        onExit();
      }
      navigate("/auth", { replace: true, state: { from: "/account/matches" } });
      return;
    }
    // Om aktivt protokoll togs bort från annat ställe (t.ex. Mina protokoll),
    // stäng shell-läget direkt i embedded vy så vi inte visar en "död" session.
    if (embedded && status === 404 && typeof onExit === "function") {
      onExit();
    }
  }, [embedded, error, id, navigate, onExit]);

  // Mutationer
  const saveHeat = useSaveHeat();
  const confirm = useConfirmMatch();
  const refreshLineup = useRefreshMatchLineup();
  const restartLineup = useRestartMatchLineup();
  const resetToInitialLineup = useResetMatchToInitialLineup();
  const updateManualLineup = useUpdateManualMatchLineup();
  const lineupSync = match?.lineup_sync || null;

  const confirmManualLineupReset = useCallback(async () => {
    if (!pendingManualLineupReset || !match?.id) return;
    const promise = resetToInitialLineup.mutateAsync({
      matchId: match.id,
      team: pendingManualLineupReset.team,
    });
    toast.promise(promise, {
      loading: "Återställer laguppställningen...",
      success: (result) =>
        pendingManualLineupReset.hasProtocolProgress || result?.mode === "hard"
          ? `Laguppställningen för ${pendingManualLineupReset.teamName} återställdes och protokollet nollställdes.`
          : `Laguppställningen för ${pendingManualLineupReset.teamName} återställdes.`,
      error: (e) => e?.message || "Kunde inte återställa laguppställningen.",
    });
    try {
      await promise;
      setPendingManualLineupReset(null);
      setManualLineupOpen(false);
      await refetch();
    } catch {
      return;
    }
  }, [match?.id, pendingManualLineupReset, refetch, resetToInitialLineup]);

  useEffect(() => {
    if (!lineupAlertFocusToken) return;
    if (!lineupAlertFocusMatchId || String(lineupAlertFocusMatchId) !== String(id || "")) return;
    if (lineupSync?.status !== "outdated") return;

    const scrollTimer = window.setTimeout(() => {
      lineupAlertRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      setLineupAlertHighlighted(true);
    }, 80);

    const clearTimer = window.setTimeout(() => {
      setLineupAlertHighlighted(false);
    }, 2600);

    return () => {
      window.clearTimeout(scrollTimer);
      window.clearTimeout(clearTimer);
    };
  }, [id, lineupAlertFocusMatchId, lineupAlertFocusToken, lineupSync?.status]);

  // Regler (tactical)
  const getRules = (m) => m?.meta?.rules ?? {};
  const tacticalRules = (m) => ({
    enabled: getRules(m).tactical?.enabled ?? true,
    start: getRules(m).tactical?.start_heat ?? 5,
    end: getRules(m).tactical?.end_heat ?? 14,
    minDef: getRules(m).tactical?.min_deficit ?? 6,
  });

  const tactical = tacticalRules(match);

  const canUseTactical = useCallback(
    (team) => {
      if (!match || !selectedHeat) return false;
      const t = tacticalRules(match);
      if (!t.enabled) return false;

      const hn = selectedHeat.heat_number;
      if (hn < t.start || hn > t.end) return false;

      const { home, away } = computeTotalsFromHeats(match);
      const diff = Math.abs(home - away);
      const losing = team === "home" ? home < away : away < home;

      return losing && diff >= t.minDef;
    },
    [match, selectedHeat]
  );

  const formatDate = (dateString) =>
    formatSvDateTime(dateString, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const formatMatchDate = (dateString) =>
    formatSvDateTime(dateString, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  const formatMatchTime = (dateString) =>
    formatSvDateTime(dateString, {
      hour: "2-digit",
      minute: "2-digit",
    });

  const openDialogFor = useCallback((heat) => {
    setSelectedHeat(heat);
    setDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setTimeout(() => setSelectedHeat(null), 0);
  }, []);

  const completedHeats =
    match?.heats?.filter((h) => h.status === "completed").length || 0;
  const canNominateHeat15 = completedHeats >= tactical.end; // Aktivera nominering när reglerna tillåter det
  const matchComplete = isMatchComplete(match);
  const alreadySaved = match?.status === "confirmed";
  const nextIncompleteHeat = useMemo(
    () => (match?.heats || []).find((h) => !isHeatSavedComplete(h)) || null,
    [match]
  );
  const heat15 = useMemo(
    () => (match?.heats || []).find((h) => Number(h?.heat_number) === 15) || null,
    [match]
  );
  const heat15HasNomination = useMemo(() => {
    if (!heat15) return false;
    const assigned = Object.values(heat15.riders || {}).filter(
      (slot) => slot && slot.rider_id != null
    );
    return assigned.length >= 4;
  }, [heat15]);
  const needsHeat15Nomination =
    Number(nextIncompleteHeat?.heat_number) === 15 && !heat15HasNomination;
  const tableTargetType = needsHeat15Nomination ? "nomination" : "heat";
  const tableTargetHeatNumber = useMemo(() => {
    if (needsHeat15Nomination) return null;
    return Number(nextIncompleteHeat?.heat_number || 0);
  }, [needsHeat15Nomination, nextIncompleteHeat]);
  const currentViewedHeatNumber = useMemo(() => {
    const viewedHeat = (match?.heats || [])[currentHeatIndex];
    return Number(viewedHeat?.heat_number || 0);
  }, [currentHeatIndex, match?.heats]);
  const isViewingNominationSlide = currentSlideMeta?.type === "nomination";
  const activeHeatNumber = protocolViewMode === "table"
    ? tableTargetHeatNumber
    : currentViewedHeatNumber;

  useEffect(() => {
    defaultViewAppliedRef.current = false;
    tableInitialPositionedRef.current = false;
  }, [id]);

  useEffect(() => {
    if (defaultViewAppliedRef.current) return;
    if (!preferenceData?.preferences) return;
    const preferredView =
      preferenceData?.preferences?.protocol_default_view === "table" ? "table" : "cards";
    setProtocolViewMode(preferredView);
    defaultViewAppliedRef.current = true;
  }, [preferenceData?.preferences]);

  useEffect(() => {
    if (protocolViewMode !== "table") return;
    if (tableInitialPositionedRef.current) return;
    if (!tableTargetHeatNumber && tableTargetType !== "nomination") return;

    setTableFocusTarget({
      type: tableTargetType,
      heatNumber: tableTargetType === "heat" ? tableTargetHeatNumber : null,
      requestKey: Date.now(),
    });
    tableInitialPositionedRef.current = true;
  }, [protocolViewMode, tableTargetHeatNumber, tableTargetType]);

  useEffect(() => {
    if (!match || tutorialLoading) return;
    const tutorialCompleted = isNativeAndroid
      ? Boolean(
          tutorialData?.tutorial?.nativeHighlights?.protocol_cards
          && tutorialData?.tutorial?.nativeHighlights?.protocol_table
        )
      : Boolean(tutorialData?.tutorial?.pages?.matchProtocol?.completed);
    const protocolDefaultViewConfirmed = Boolean(
      preferenceData?.preferences?.protocol_default_view_confirmed
    );
    setShowProtocolViewPrompt(tutorialCompleted && !protocolDefaultViewConfirmed);
  }, [
    match,
    preferenceData?.preferences?.protocol_default_view_confirmed,
    tutorialData?.tutorial?.pages?.matchProtocol?.completed,
    tutorialData?.tutorial?.nativeOnboardingVersion,
    tutorialData?.tutorial?.nativeHighlights?.protocol_cards,
    tutorialData?.tutorial?.nativeHighlights?.protocol_table,
    tutorialLoading,
    isNativeAndroid,
  ]);

  const matchProtocolSteps = useMemo(
    () => matchProtocolTutorialState?.steps || [],
    [matchProtocolTutorialState?.steps]
  );
  const tableViewStepIndex = useMemo(
    () => matchProtocolSteps.findIndex((step) => step?.target === "#matchProtocolTableView"),
    [matchProtocolSteps]
  );

  useEffect(() => {
    if (!matchProtocolTutorialState?.run) return;

    if (tableViewStepIndex >= 0 && matchProtocolTutorialState.stepIndex >= tableViewStepIndex) {
      if (protocolViewMode !== "table") {
        setProtocolViewMode("table");
        tableInitialPositionedRef.current = true;
        if (nextIncompleteHeat?.heat_number || needsHeat15Nomination) {
          setTableFocusTarget({
            type: needsHeat15Nomination ? "nomination" : "heat",
            heatNumber: needsHeat15Nomination ? null : Number(nextIncompleteHeat?.heat_number),
            requestKey: Date.now(),
          });
        }
      }
      return;
    }

    if (protocolViewMode !== "cards") {
      setProtocolViewMode("cards");
    }
  }, [
    matchProtocolTutorialState?.run,
    matchProtocolTutorialState?.stepIndex,
    nextIncompleteHeat?.heat_number,
    needsHeat15Nomination,
    protocolViewMode,
    tableViewStepIndex,
  ]);
  useEffect(() => {
    const prev = prevCompletedHeatsRef.current;
    if (completedHeats >= 15 && prev < 15 && !alreadySaved) {
      if (completeProtocolPromptTimeoutRef.current) {
        clearTimeout(completeProtocolPromptTimeoutRef.current);
      }
      completeProtocolPromptTimeoutRef.current = setTimeout(() => {
        setCompleteProtocolPromptOpen(true);
        completeProtocolPromptTimeoutRef.current = null;
      }, 350);
    }
    prevCompletedHeatsRef.current = completedHeats;

    return () => {
      if (completeProtocolPromptTimeoutRef.current) {
        clearTimeout(completeProtocolPromptTimeoutRef.current);
        completeProtocolPromptTimeoutRef.current = null;
      }
    };
  }, [completedHeats, alreadySaved]);

  const handleOpenHeatFromTable = useCallback((heatOrNumber) => {
    const heatNumber = Number(heatOrNumber?.heat_number ?? heatOrNumber);
    const heat = (match?.heats || []).find(
      (candidate) => Number(candidate?.heat_number) === heatNumber
    );
    if (!heat) return;

    const nextRequiredHeatNumber = Number(nextIncompleteHeat?.heat_number || 0);
    if (nextRequiredHeatNumber > 0 && heatNumber > nextRequiredHeatNumber) {
      toast("Kommande heat är låst", {
        description: `Fyll i heat ${nextRequiredHeatNumber} innan du går vidare till senare heat.`,
        icon: <AlertTriangle className="h-4 w-4" />,
      });
      return;
    }

    if (heatNumber === 15 && !heat15HasNomination) {
      setNomOpen(true);
      return;
    }

    openDialogFor(heat);
  }, [heat15HasNomination, match?.heats, nextIncompleteHeat?.heat_number, openDialogFor]);

  const handleOpenNominationFromTable = useCallback(() => {
    if (heat15HasNomination || canNominateHeat15) {
      setNomOpen(true);
      return;
    }

    toast("Nominering är inte tillgänglig än", {
      description: "Heat 14 måste vara klart innan du kan nominera heat 15.",
      icon: <AlertTriangle className="h-4 w-4" />,
    });
  }, [canNominateHeat15, heat15HasNomination]);

  const handleContinueDraft = useCallback(() => {
    if (!nextIncompleteHeat) {
      toast("Alla heat är ifyllda", {
        description: "Du kan nu spara till Sparade protokoll.",
      });
      return;
    }
    if (protocolViewMode === "table") {
      if (Number(nextIncompleteHeat?.heat_number) === 15 && !heat15HasNomination) {
        if (!tableIsFocusedHeatInView) {
          setTableFocusTarget({ type: "nomination", heatNumber: null, requestKey: Date.now() });
          return;
        }
        setNomOpen(true);
        return;
      }
      if (!tableIsFocusedHeatInView) {
        setTableFocusTarget({
          type: "heat",
          heatNumber: Number(nextIncompleteHeat?.heat_number),
          requestKey: Date.now(),
        });
        return;
      }
      openDialogFor(nextIncompleteHeat);
      return;
    }
    if (Number(nextIncompleteHeat?.heat_number) === 15 && !heat15HasNomination) {
      if (currentViewedHeatNumber !== 15) {
        setFocusTarget({ type: "nomination", requestKey: Date.now() });
        return;
      }
      setNomOpen(true);
      return;
    }
    if (Number(nextIncompleteHeat?.heat_number) === 15 && isViewingNominationSlide) {
      setFocusTarget({
        type: "heat",
        heatNumber: 15,
        requestKey: Date.now(),
      });
      return;
    }
    if (currentViewedHeatNumber !== Number(nextIncompleteHeat?.heat_number)) {
      setFocusTarget({
        type: "heat",
        heatNumber: Number(nextIncompleteHeat?.heat_number),
        requestKey: Date.now(),
      });
      return;
    }
    openDialogFor(nextIncompleteHeat);
  }, [
    currentViewedHeatNumber,
    isViewingNominationSlide,
    nextIncompleteHeat,
    heat15HasNomination,
    openDialogFor,
    protocolViewMode,
    tableIsFocusedHeatInView,
  ]);

  const handleChooseProtocolDefaultView = useCallback(
    async (nextView) => {
      try {
        await updatePreferences.mutateAsync({
          preferences: {
            auto_scroll_next_heat:
              preferenceData?.preferences?.auto_scroll_next_heat !== false,
            allow_haptics: preferenceData?.preferences?.allow_haptics !== false,
            haptic_intensity: preferenceData?.preferences?.haptic_intensity || "normal",
            protocol_default_view: nextView,
            protocol_default_view_confirmed: true,
          },
        });
        setProtocolViewMode(nextView);
        if (nextView === "table" && (nextIncompleteHeat?.heat_number || needsHeat15Nomination)) {
          tableInitialPositionedRef.current = true;
          setTableFocusTarget({
            type: needsHeat15Nomination ? "nomination" : "heat",
            heatNumber: needsHeat15Nomination ? null : Number(nextIncompleteHeat.heat_number),
            requestKey: Date.now(),
          });
        }
        setShowProtocolViewPrompt(false);
        toast.success(
          nextView === "table"
            ? "Tabellvy vald som standard."
            : "Heatkort vald som standard."
        );
      } catch (error) {
        toast.error(error?.message || "Kunde inte spara standardvyn.");
      }
    },
    [
      nextIncompleteHeat?.heat_number,
      needsHeat15Nomination,
      preferenceData?.preferences?.allow_haptics,
      preferenceData?.preferences?.auto_scroll_next_heat,
      updatePreferences,
    ]
  );

  const continueDraftLabel = useMemo(() => {
    if (needsHeat15Nomination) {
      if (protocolViewMode === "table") {
        return tableIsFocusedHeatInView ? "Nominera heat 15" : "Gå till nominering";
      }
      return activeHeatNumber === 15 ? "Nominera heat 15" : "Gå till heat 15";
    }
    if (
      nextIncompleteHeat &&
      (
        (protocolViewMode === "table"
          ? !tableIsFocusedHeatInView
          : activeHeatNumber !== Number(nextIncompleteHeat.heat_number)) ||
        (protocolViewMode !== "table" &&
          Number(nextIncompleteHeat.heat_number) === 15 &&
          isViewingNominationSlide)
      )
    ) {
      return `Gå till heat ${nextIncompleteHeat.heat_number}`;
    }
    return "Fortsätt fyll i protokoll";
  }, [activeHeatNumber, isViewingNominationSlide, needsHeat15Nomination, nextIncompleteHeat, protocolViewMode, tableIsFocusedHeatInView]);

  const continueDraftUi = useMemo(() => {
    if (needsHeat15Nomination) {
      if (protocolViewMode === "table" ? tableIsFocusedHeatInView : activeHeatNumber === 15) {
        return {
          label: "Nominera heat 15",
          icon: Crosshair,
          variant: "default",
        };
      }
      return {
        label: "Gå till nominering",
        icon: protocolViewMode === "table" ? Crosshair : activeHeatNumber < 15 ? ArrowRight : ArrowLeft,
        variant: "outline",
      };
    }
    if (
      nextIncompleteHeat &&
      (
        (protocolViewMode === "table"
          ? !tableIsFocusedHeatInView
          : activeHeatNumber !== Number(nextIncompleteHeat.heat_number)) ||
        (protocolViewMode !== "table" &&
          Number(nextIncompleteHeat.heat_number) === 15 &&
          isViewingNominationSlide)
      )
    ) {
      const targetHeatNumber = Number(nextIncompleteHeat.heat_number);
      const shouldUseForwardIcon =
        protocolViewMode === "table"
          ? false
          : Number(nextIncompleteHeat.heat_number) === 15 &&
            isViewingNominationSlide
          ? true
          : activeHeatNumber < targetHeatNumber;
      return {
        label: `Gå till heat ${nextIncompleteHeat.heat_number}`,
        icon: protocolViewMode === "table" ? Crosshair : shouldUseForwardIcon ? ArrowRight : ArrowLeft,
        variant: "outline",
      };
    }
    return {
      label: continueDraftLabel,
      icon: Play,
      variant: "default",
    };
  }, [continueDraftLabel, activeHeatNumber, isViewingNominationSlide, needsHeat15Nomination, nextIncompleteHeat, protocolViewMode, tableIsFocusedHeatInView]);

  const displayedContinueDraftUi = continueDraftUi;
  const protocolViewToggle = (
    <div
      id="protocolViewToggle"
      className="native-v3-running-view-toggle inline-flex h-10 w-[184px] items-center overflow-hidden rounded-full border border-border bg-background p-1 shadow-inner"
    >
      <Button
        type="button"
        data-native-onboarding-target="protocol-cards"
        size="sm"
        variant={protocolViewMode === "cards" ? "default" : "ghost"}
        onClick={() => setProtocolViewMode("cards")}
        aria-pressed={protocolViewMode === "cards"}
        aria-label="Visa heatkort"
        className={cn(
          "h-8 min-w-0 flex-1 justify-center gap-1.5 overflow-hidden rounded-full px-2 transition-colors duration-200 ease-out",
          protocolViewMode !== "cards" && "text-muted-foreground hover:text-foreground"
        )}
      >
        <Rows3 className="h-4 w-4 shrink-0" />
        <span
          className={cn(
            "whitespace-nowrap text-xs"
          )}
        >
          Heatkort
        </span>
      </Button>
      <Button
        type="button"
        data-native-onboarding-target="protocol-table"
        size="sm"
        variant={protocolViewMode === "table" ? "default" : "ghost"}
        onClick={() => {
          setProtocolViewMode("table");
          tableInitialPositionedRef.current = true;
          if (nextIncompleteHeat?.heat_number || needsHeat15Nomination) {
            setTableFocusTarget({
              type: needsHeat15Nomination ? "nomination" : "heat",
              heatNumber: needsHeat15Nomination ? null : Number(nextIncompleteHeat.heat_number),
              requestKey: Date.now(),
            });
          }
        }}
        aria-pressed={protocolViewMode === "table"}
        aria-label="Visa tabellvy"
        className={cn(
          "h-8 min-w-0 flex-1 justify-center gap-1.5 overflow-hidden rounded-full px-2 transition-colors duration-200 ease-out",
          protocolViewMode !== "table" && "text-muted-foreground hover:text-foreground"
        )}
      >
        <Table2 className="h-4 w-4 shrink-0" />
        <span
          className={cn(
            "whitespace-nowrap text-xs"
          )}
        >
          Tabellvy
        </span>
      </Button>
    </div>
  );

  // Spara (anropas av HeatDialog)
  const handleSave = useCallback(
    // async ({ heat_number, assignments, results }) => {
    async ({ heat_number, assignments, results, rr = false, heat_time = "" }) => {
      if (!getToken()) {
        alert("Du måste vara inloggad för att spara resultat.");
        return;
      }
      if (!match?.id || !heat_number) return;

      await saveHeat.mutateAsync({
        matchId: match.id,
        heatNumber: heat_number,
        assignments,
        results,
        rr,
        heatTime: heat_time,
      });

      const cachedMatch = queryClient.getQueryData(["match", match.id]);
      const latestMatch =
        cachedMatch && Array.isArray(cachedMatch?.heats)
          ? cachedMatch
          : (await refetch())?.data;

      if (protocolViewMode === "table" && autoScrollNextHeatEnabled) {
        const nextHeat = (latestMatch?.heats || []).find((heat) => !isHeatSavedComplete(heat));
        const refreshedHeat15 = (latestMatch?.heats || []).find(
          (heat) => Number(heat?.heat_number) === 15
        );
        const refreshedHeat15HasNomination =
          Object.values(refreshedHeat15?.riders || {}).filter(
            (slot) => slot && slot.rider_id != null
          ).length >= 4;

        if (Number(nextHeat?.heat_number) === 15 && !refreshedHeat15HasNomination) {
          setTableFocusTarget({
            type: "nomination",
            heatNumber: null,
            requestKey: Date.now(),
          });
        } else if (nextHeat?.heat_number) {
          setTableFocusTarget({
            type: "heat",
            heatNumber: Number(nextHeat.heat_number),
            requestKey: Date.now(),
          });
        }
      }
    },
    [autoScrollNextHeatEnabled, match, match?.id, protocolViewMode, queryClient, refetch, saveHeat]
  );



  // Spara/confirm protokoll
  const finalizeProtocol = useCallback(async ({ forceComplete = false } = {}) => {
    setSaving(true);
    try {
      const res = await toast.promise(confirm.mutateAsync({
        id,
        payload: forceComplete ? { force_complete: true } : {},
      }), {
        loading: "Sparar...",
        success: forceComplete ? "Protokoll slutsparat" : "Protokoll sparat",
        error: (e) => e?.detail || "Kunde inte spara protokollet",
      });

      const userMatchId = res?.user_match_id;
      const destination = userMatchId
        ? `/account/matches/saved/${userMatchId}`
        : "/account/matches";
      const navOptions = { replace: true, state: { justSaved: true } };

      if (res?.discrepancies?.length) {
        const text = res.discrepancies
          .map((d) => `${d.type}: ${d.user_value} → ${d.official_value}`)
          .join(" • ");
        toast("Skillnader mot officiella resultat", {
          description: text,
          closeButton: true,
          duration: 12000,
          action: userMatchId
            ? {
              label: "Visa detaljer",
              onClick: () => navigate(destination, navOptions),
            }
            : undefined,
        });
      }

      // navigate("/account/matches", { replace: true, state: { justSaved: true } });
      if (embedded && typeof onExit === "function") {
        onExit();
      }
      navigate(destination, navOptions);
    } finally {
      setSaving(false);
    }
  }, [id, confirm, navigate, embedded, onExit]);

  const doSave = useCallback(async () => {
    setCompleteProtocolPromptOpen(false);
    await finalizeProtocol();
  }, [finalizeProtocol]);

  const doForceSave = useCallback(async () => {
    await finalizeProtocol({ forceComplete: true });
  }, [finalizeProtocol]);

  const handleRefreshProtocolLineup = useCallback(async () => {
    if (!match?.id) return;
    const promise = refreshLineup.mutateAsync(match.id);
    toast.promise(promise, {
      loading: "Uppdaterar laguppställning...",
      success: "Protokollet uppdaterades till senaste lineup.",
      error: (e) => e?.message || "Kunde inte uppdatera protokollet.",
    });
    try {
      await promise;
      await refetch();
    } catch {
      return;
    }
  }, [match?.id, refreshLineup, refetch]);

  const handleRestartProtocolLineup = useCallback(async () => {
    if (!match?.id) return;
    const promise = restartLineup.mutateAsync(match.id);
    toast.promise(promise, {
      loading: "Börjar om protokollet...",
      success: "Protokollet återställdes till senaste lineup.",
      error: (e) => e?.message || "Kunde inte börja om protokollet.",
    });
    try {
      await promise;
      await refetch();
    } catch {
      return;
    }
  }, [match?.id, restartLineup, refetch]);

  const handleSaveManualLineup = useCallback(async () => {
    if (!match?.id) return;
    const lineups = {
      home: buildManualLineupPayload(manualHomeState, manualHomeOptions),
      away: buildManualLineupPayload(manualAwayState, manualAwayOptions),
    };
    const promise = updateManualLineup.mutateAsync({ matchId: match.id, lineups });
    toast.promise(promise, {
      loading: "Sparar laguppställning...",
      success: "Laguppställningen uppdaterades.",
      error: (e) => e?.message || "Kunde inte uppdatera laguppställningen.",
    });
    try {
      await promise;
      closeManualLineupModal();
      await refetch();
    } catch {
      return;
    }
  }, [
    closeManualLineupModal,
    manualAwayOptions,
    manualAwayState,
    manualHomeOptions,
    manualHomeState,
    match?.id,
    refetch,
    updateManualLineup,
  ]);

  // ===== UI Helpers =====
  const getStatusBadge = (status) => {
    switch (status) {
      case "confirmed":
        return <Badge className="native-v3-status-badge">Bekräftad</Badge>;
      case "reopened":
        return <Badge className="native-v3-status-badge">Återöppnat</Badge>;
      case "live":
        return <Badge className="native-v3-status-badge" variant="destructive">Pågår</Badge>;
      case "completed":
        return <Badge className="native-v3-status-badge" variant="secondary">Avslutad</Badge>;
      case "upcoming":
      default:
        return <Badge className="native-v3-status-badge" variant="secondary">Kommande</Badge>;
    }
  };

  // const getPositionColor = (position) => {
  //   switch (position) {
  //     case 1:
  //       return "bg-yellow-500 text-black";
  //     case 2:
  //       return "bg-gray-400 text-white";
  //     case 3:
  //       return "bg-amber-600 text-white";
  //     case 4:
  //       return "bg-gray-600 text-white";
  //     default:
  //       return "bg-gray-200 text-gray-700";
  //   }
  // };

  function isHeatSavedComplete(heat) {
    if (!heat || !Array.isArray(heat.results) || heat.results.length === 0)
      return false;
    const used = new Set();
    for (const gate of Object.keys(heat.riders).sort()) {
      const riderId = heat.riders[gate].rider_id;
      const res = heat.results.find(
        (r) => String(r.rider_id) === String(riderId)
      );
      if (!res) return false;
      if (res.status === "completed") {
        const pos = Number(res.position);
        if (!Number.isInteger(pos) || pos < 1 || pos > 4) return false;
        if (used.has(pos)) return false;
        used.add(pos);
      } else if (!res.status) {
        return false;
      }
    }
    return true;
  }

  const computeHeatBonuses = (heat) => {
    const bonuses = {};
    if (!heat || !Array.isArray(heat.results)) return bonuses;
    const finished = heat.results
      .filter((r) => r.status === "completed" && typeof r.position === "number")
      .sort((a, b) => a.position - b.position);
    if (finished.length < 3) return bonuses;
    const riderTeams = {};
    Object.values(heat.riders).forEach(
      (r) => (riderTeams[r.rider_id] = r.team)
    );
    if (riderTeams[finished[0].rider_id] === riderTeams[finished[1].rider_id]) {
      bonuses[finished[1].rider_id] = 1;
    }
    if (riderTeams[finished[1].rider_id] === riderTeams[finished[2].rider_id]) {
      bonuses[finished[2].rider_id] = 1;
    }
    return bonuses;
  };

  const computeTotalsFromHeats = (m) => {
    let home = 0,
      away = 0;
    if (!m?.heats) return { home, away };
    for (const h of m.heats) {
      if (!Array.isArray(h.results)) continue;
      for (const res of h.results) {
        const pts = Number(res.points) || 0;
        const riderEntry = Object.values(h.riders || {}).find(
          (g) => String(g?.rider_id) === String(res.rider_id)
        );
        const team = riderEntry?.team;
        if (team === "home") home += pts;
        if (team === "away") away += pts;
      }
    }
    return { home, away };
  };

  const { home: homeScore, away: awayScore } = useMemo(
    () => computeTotalsFromHeats(match),
    [match]
  );


  const matchStats = useMemo(() => {
    const baseStats = {
      gateWins: {},
      bestGates: [],
      teams: {
        home: {
          key: "home",
          name: match?.home_team || "Hemmalag",
          base: 0,
          bonus: 0,
          total: 0,
          riders: {},
        },
        away: {
          key: "away",
          name: match?.away_team || "Bortalag",
          base: 0,
          bonus: 0,
          total: 0,
          riders: {},
        },
      },
      heatOutcomes: { home: 0, away: 0, draws: 0 },
      fiveOneHeats: { home: 0, away: 0 },
      exclusions: 0,
    };

    if (!Array.isArray(match?.heats)) {
      return baseStats;
    }

    const allGates = new Set();

    match.heats.forEach((heat) => {
      if (!heat) return;
      const riderLookup = {};

      Object.entries(heat.riders || {}).forEach(([gate, riderInfo]) => {
        allGates.add(gate);
        if (baseStats.gateWins[gate] == null) {
          baseStats.gateWins[gate] = 0;
        }
        if (!riderInfo?.rider_id) return;
        const rid = String(riderInfo.rider_id);
        riderLookup[rid] = {
          gate,
          team: riderInfo.team,
          name:
            riderInfo.name ||
            riderInfo.full_name ||
            riderInfo.rider_name ||
            riderInfo?.label,
        };
      });

      const heatTotals = { home: 0, away: 0 };
      let winningGate = null;
      const orderedResults = (heat.results || [])
        .filter(
          (r) => r?.status === "completed" && typeof r?.position === "number"
        )
        .sort((a, b) => a.position - b.position);

      (heat.results || []).forEach((result) => {
        if (!result) return;
        const riderId = String(result.rider_id ?? "");
        const meta = riderLookup[riderId];

        if (result.status && result.status !== "completed") {
          baseStats.exclusions += 1;
        }

        if (!meta) {
          return;
        }

        const basePoints = Number(result.points) || 0;
        const bonusPoints = Number(result.bonus_points) || 0;

        if (meta.team === "home" || meta.team === "away") {
          const team = baseStats.teams[meta.team];

          const existing = team.riders[riderId] || {
            id: riderId,
            name: meta.name || result.rider_name || `Förare ${riderId}`,
            base: 0,
            bonus: 0,
            total: 0,
          };

          if (result.status === "completed") {
            team.base += basePoints;
            existing.base += basePoints;
            heatTotals[meta.team] += basePoints;

            if (bonusPoints) {
              team.bonus += bonusPoints;
              existing.bonus += bonusPoints;
            }
          }

          team.total = team.base + team.bonus;
          existing.total = existing.base + existing.bonus;
          team.riders[riderId] = existing;
        }

        if (Number(result.position) === 1 && meta.gate) {
          winningGate = meta.gate;
        }
      });

      if (orderedResults.length >= 2) {
        const first = orderedResults[0];
        const second = orderedResults[1];
        const firstMeta = riderLookup[String(first.rider_id ?? "")];
        const secondMeta = riderLookup[String(second.rider_id ?? "")];
        const firstBase = Number(first.points) || 0;
        const secondBase = Number(second.points) || 0;
        if (
          firstMeta?.team &&
          firstMeta.team === secondMeta?.team &&
          firstBase === 3 &&
          secondBase === 2
        ) {
          const opponent = orderedResults.find((res) => {
            const meta = riderLookup[String(res.rider_id ?? "")];
            return meta?.team && meta.team !== firstMeta.team;
          });

          if (opponent) {
            const opponentBase = Number(opponent.points) || 0;
            if (opponentBase === 1) {
              const key = firstMeta.team;
              if (
                Object.prototype.hasOwnProperty.call(baseStats.fiveOneHeats, key)
              ) {
                baseStats.fiveOneHeats[key] += 1;
              }
            }
          }
        }
      }

      if (winningGate) {
        baseStats.gateWins[winningGate] =
          (baseStats.gateWins[winningGate] || 0) + 1;
      }

      if (heatTotals.home > heatTotals.away) {
        baseStats.heatOutcomes.home += 1;
      } else if (heatTotals.away > heatTotals.home) {
        baseStats.heatOutcomes.away += 1;
      } else if (heatTotals.home || heatTotals.away) {
        baseStats.heatOutcomes.draws += 1;
      }
    });

    allGates.forEach((gate) => {
      if (baseStats.gateWins[gate] == null) {
        baseStats.gateWins[gate] = 0;
      }
    });

    const gateValues = Object.values(baseStats.gateWins);
    const maxWins = gateValues.length ? Math.max(...gateValues) : 0;
    baseStats.bestGates =
      maxWins > 0
        ? Object.entries(baseStats.gateWins)
          .filter(([, wins]) => wins === maxWins)
          .map(([gate]) => gate)
        : [];

    Object.values(baseStats.teams).forEach((team) => {
      team.total = team.base + team.bonus;
    });

    return baseStats;
  }, [match]);

  const hasHeats = Array.isArray(match?.heats) && match.heats.length > 0;

  // === Render ===
  // Returnera skelett under laddning så layouten inte hoppar
  if (loading) {
    return <MatchProtocolSkeleton native={isNativeAndroid} />;
  }

  if (error) {
    const message = error?.message || "Något gick fel";
    return (
      <div className="p-6">
        <div className="p-4 rounded-md border border-red-2 00 bg-red-50 text-red-700">
          {message}
        </div>
        <div className="mt-4">
          <Button
            onClick={() => {
              if (embedded && typeof onExit === "function") onExit();
              else navigate("/matches");
            }}
          >
            Till matcher
          </Button>
        </div>
      </div>
    );
  }

  if (!match) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-16">
          <Target className="w-16 h-16 text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">
            Inget matchprotokoll aktivt
          </h3>
          <p className="text-gray-500 text-center max-w-md mb-4">
            Öppna en match från sidan “Matcher” för att börja föra protokoll.
          </p>
          <Button
            onClick={() => {
              if (embedded && typeof onExit === "function") onExit();
              else navigate("/matches");
            }}
          >
            Gå till Matcher
          </Button>
        </CardContent>
      </Card>
    );
  }

  const primaryActionArea = (
    <div
      id="protocolPrimaryActionArea"
      className={cn(
        "z-10",
        embedded && embeddedFooterId
          ? "w-full"
          : embedded
            ? "fixed inset-x-0 bottom-0"
            : "sticky bottom-0 -mx-4 sm:-mx-6"
      )}
    >
      <Card className="native-v3-running-primary-card rounded-t-2xl rounded-b-none border-x-0 border-b-0 border-t border-secondary/60 bg-card/95 shadow-[0_-10px_30px_rgba(15,23,42,0.08)] backdrop-blur-sm">
        <CardContent className="px-4 pb-[calc(env(safe-area-inset-bottom)+0.5rem)] pt-3 sm:px-6 sm:pt-4">
          {alreadySaved ? (
            <Button className="w-full" variant="secondary" disabled>
              Redan sparat
            </Button>
          ) : matchComplete ? (
            <ConfirmButton
              title="Spara protokoll?"
              description="Protokollet hamnar i Mina sparade protokoll."
              confirmText="Spara"
              cancelText="Avbryt"
              triggerVariant="default"
              actionVariant="default"
              onConfirm={doSave}
              disabled={saving}
              id="protocolPrimaryAction"
              className="w-full min-w-[180px]"
            >
              <Save className="mr-2 h-4 w-4" />
              {saving ? "Sparar protokoll..." : "Spara till Sparade protokoll"}
            </ConfirmButton>
          ) : (
            <div className="space-y-1.5">
              <Button
                type="button"
                id="protocolPrimaryAction"
                variant={displayedContinueDraftUi.variant}
                className="w-full min-w-[180px]"
                onClick={handleContinueDraft}
              >
                <displayedContinueDraftUi.icon className="mr-2 h-4 w-4" />
                <span>{displayedContinueDraftUi.label}</span>
              </Button>

              <ConfirmButton
                title="Slutspara protokollet nu?"
                description="Använd detta om matchen avslutades tidigare. Protokollet sparas som avslutat utifrån ditt nuvarande resultat."
                confirmText="Slutspara nu"
                cancelText="Avbryt"
                triggerVariant="ghost"
                actionVariant="default"
                onConfirm={doForceSave}
                disabled={saving}
                className="w-full text-sm text-muted-foreground hover:text-foreground"
              >
                <AlertTriangle className="mr-2 h-4 w-4" />
                {saving ? "Sparar protokoll..." : "Slutspara nu"}
              </ConfirmButton>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const renderedPrimaryActionArea = embedded && embeddedFooterId
    ? embeddedFooterTarget
      ? createPortal(primaryActionArea, embeddedFooterTarget)
      : null
    : primaryActionArea;

  return (
    <div
      ref={pageRootRef}
      className={cn(
        "space-y-2",
        embedded && !embeddedFooterId && "pb-24",
        isNativeAndroid && "native-v3-running-protocol"
      )}
    >
      <PageTutorial
        pageName="matchProtocol"
        autoStart={!loading && Boolean(match) && !matchComplete}
        onStepChange={setMatchProtocolTutorialState}
      />
      <AlertDialog open={showProtocolViewPrompt}>
        <AlertDialogContent className={isNativeAndroid ? "native-v3-protocol-alert native-v3-running-view-prompt" : undefined}>
          <AlertDialogHeader>
            <AlertDialogTitle>Välj hur matchprotokoll ska öppnas</AlertDialogTitle>
            <AlertDialogDescription>
              Välj vilken vy du vill använda som standard när du startar eller återupptar ett matchprotokoll.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid gap-3">
            <button
              type="button"
              className="rounded-2xl border bg-card p-4 text-left transition hover:border-primary/60 hover:bg-muted/40"
              onClick={() => handleChooseProtocolDefaultView("cards")}
              disabled={updatePreferences.isPending}
            >
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-primary/10 p-2 text-primary">
                  <Rows3 className="h-5 w-5" />
                </div>
                <div className="space-y-1">
                  <div className="text-base font-semibold">Använd Heatkort</div>
                  <div className="text-sm text-muted-foreground">
                    Ett heat i taget. Passar bäst om du vill arbeta stegvis och fokusera på nuvarande heat.
                  </div>
                </div>
              </div>
            </button>
            <button
              type="button"
              className="rounded-2xl border bg-card p-4 text-left transition hover:border-primary/60 hover:bg-muted/40"
              onClick={() => handleChooseProtocolDefaultView("table")}
              disabled={updatePreferences.isPending}
            >
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-amber-500/10 p-2 text-amber-600">
                  <Table2 className="h-5 w-5" />
                </div>
                <div className="space-y-1">
                  <div className="text-base font-semibold">Använd Tabellvy</div>
                  <div className="text-sm text-muted-foreground">
                    En sammanfattande översikt där alla heat syns i samma vy. Passar om du vill se helheten direkt.
                  </div>
                </div>
              </div>
            </button>
          </div>
          <AlertDialogFooter className="sm:justify-start">
            <p className="text-xs text-muted-foreground">
              Du kan ändra detta när som helst under Utseende på Mina sidor.
            </p>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={completeProtocolPromptOpen} onOpenChange={setCompleteProtocolPromptOpen}>
        <AlertDialogContent className={cn("max-w-lg overflow-visible", isNativeAndroid && "native-v3-protocol-alert native-v3-running-complete-dialog")}>
          <div className="native-v3-running-complete-confetti pointer-events-none absolute left-1/2 top-0 z-[120] -translate-x-1/2 -translate-y-[60%] sm:-translate-y-[48%]">
            <DotLottieReact
              src="/lottie/Confetti.lottie"
              autoplay
              loop={false}
              className="h-[420px] w-[min(145vw,560px)] sm:h-[500px] sm:w-[600px]"
            />
          </div>
          {isNativeAndroid ? (
            <div className="native-v3-running-complete-heading">
              <CheckCircle2 aria-hidden="true" />
              <span>Protokollet är klart</span>
            </div>
          ) : null}
          <div className="native-v3-running-complete-score">
            <ProtocolScoreboard
              match={match}
              homeScore={homeScore}
              awayScore={awayScore}
              completedHeats={completedHeats}
              formatMatchDate={formatMatchDate}
              formatMatchTime={formatMatchTime}
              showFooter={false}
            />
          </div>
          <AlertDialogHeader className="native-v3-running-complete-copy">
            <AlertDialogTitle>Vill du spara ditt färdiga protokoll?</AlertDialogTitle>
            <AlertDialogDescription>
              Protokollet är nu komplett. Vill du spara det till Sparade protokoll nu?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="native-v3-protocol-alert-footer native-v3-running-complete-actions">
            <Button
              type="button"
              variant="ghost"
              className="native-v3-protocol-alert-cancel"
              onClick={() => setCompleteProtocolPromptOpen(false)}
              disabled={saving}
            >
              Inte nu
            </Button>
            <Button
              type="button"
              className="native-v3-protocol-alert-primary"
              onClick={doSave}
              disabled={saving}
            >
              {saving ? "Sparar..." : "Spara protokoll"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Dialog
        open={manualLineupOpen}
        onOpenChange={(open) => {
          if (open) {
            setManualLineupOpen(true);
            return;
          }
          requestCloseManualLineupModal();
        }}
      >
        <DialogContent className={cn("flex max-h-[85vh] max-w-2xl flex-col overflow-hidden p-0", isNativeAndroid && "native-v3-running-dialog")}>
          <DialogHeader className={cn("px-4 pb-3 pt-4 sm:px-5", isNativeAndroid && "native-v3-lineup-header")}>
            <DialogTitle>Ändra laguppställning</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Uppdatera laguppställningen manuellt för det här protokollet. Kommande heat byggs om utifrån den nya
              uppställningen, medan redan registrerade heat lämnas oförändrade.
            </DialogDescription>
          </DialogHeader>
          <div className={cn("flex-1 overflow-y-auto px-4 pb-4 sm:px-5", isNativeAndroid && "native-v3-lineup-body")}>
            {isNativeAndroid ? (
              <div className="native-v3-lineup-tabs" role="tablist" aria-label="Välj laguppställning">
                <button
                  type="button"
                  role="tab"
                  aria-selected={manualLineupTeam === "home"}
                  data-state={manualLineupTeam === "home" ? "active" : "inactive"}
                  onClick={() => setManualLineupTeam("home")}
                >
                  {match?.home_team || "Hemmalag"}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={manualLineupTeam === "away"}
                  data-state={manualLineupTeam === "away" ? "active" : "inactive"}
                  onClick={() => setManualLineupTeam("away")}
                >
                  {match?.away_team || "Bortalag"}
                </button>
              </div>
            ) : null}
            <div className="grid gap-4 md:grid-cols-2">
              <section className={cn("grid gap-2.5", isNativeAndroid && "native-v3-lineup-team", isNativeAndroid && manualLineupTeam !== "home" && "hidden")}>
                {(!isNativeAndroid || manualHomeCanReset) ? <div className={cn("flex items-center justify-between gap-3", isNativeAndroid && "native-v3-lineup-team-heading")}>
                  <div className="text-sm font-semibold text-foreground">{match?.home_team || "Hemmalag"}</div>
                  {manualHomeCanReset ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 gap-1.5 px-2 text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => handleResetManualLineupTeam("home")}
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Återställ
                    </Button>
                  ) : null}
                </div> : null}
              {MANUAL_LINEUP_SLOTS.map((slot) => (
                <div key={`manual-home-${slot}`} className={cn("flex items-center gap-2", isNativeAndroid && "native-v3-lineup-slot")}>
                  {(() => {
                    const draftChange = manualHomeDraftChanges[String(slot)];
                    return (
                      <>
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <div className="w-5 text-xs font-medium text-muted-foreground sm:text-sm">{slot}.</div>
                    <Select
                      value={String(manualHomeState[String(slot)] || "")}
                      onValueChange={(value) => handleManualLineupSelect("home", slot, value)}
                    >
                      <SelectTrigger className="h-8 w-full border-border bg-card px-3 text-sm">
                        {getManualLineupTriggerContent(
                          manualHomeState[String(slot)],
                          manualHomeOptionMap,
                          draftChange,
                          match?.home_team_id
                        )}
                      </SelectTrigger>
                      <SelectContent className={cn("z-[100] border-border bg-card text-card-foreground shadow-xl", isNativeAndroid && "native-v3-lineup-select")}>
                        {manualHomeOptions.map((rider) => {
                          const occupiedSlot = findManualLineupRiderSlot(manualHomeState, rider.id, slot);
                          const isSwapOption = Boolean(occupiedSlot);
                          return (
                            <SelectItem
                              key={`manual-home-option-${slot}-${rider.id}`}
                              value={String(rider.id)}
                              className={cn(
                                "bg-card",
                                isSwapOption && "text-amber-700 focus:text-amber-800 dark:text-amber-300 dark:focus:text-amber-200"
                              )}
                            >
                              <div className="flex w-full min-w-0 flex-nowrap items-center gap-3">
                                <span className="flex min-w-0 flex-1 flex-nowrap items-center gap-2 overflow-hidden whitespace-nowrap">
                                  <span className="truncate">{rider.name}</span>
                                  {isGuestRiderForTeam(rider, match?.home_team_id) ? <GuestRiderBadge className="shrink-0" /> : null}
                                </span>
                                {isSwapOption ? (
                                  <span className="ml-auto inline-flex shrink-0 items-center gap-1 text-[11px] font-medium">
                                    <Repeat2 className="h-3.5 w-3.5" />
                                    Byt med nr {occupiedSlot}
                                  </span>
                                ) : null}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                      </>
                    );
                  })()}
                </div>
              ))}
              </section>
              <section className={cn("grid gap-2.5", isNativeAndroid && "native-v3-lineup-team", isNativeAndroid && manualLineupTeam !== "away" && "hidden")}>
                {(!isNativeAndroid || manualAwayCanReset) ? <div className={cn("flex items-center justify-between gap-3", isNativeAndroid && "native-v3-lineup-team-heading")}>
                  <div className="text-sm font-semibold text-foreground">{match?.away_team || "Bortalag"}</div>
                  {manualAwayCanReset ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 gap-1.5 px-2 text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => handleResetManualLineupTeam("away")}
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Återställ
                    </Button>
                  ) : null}
                </div> : null}
              {MANUAL_LINEUP_SLOTS.map((slot) => (
                <div key={`manual-away-${slot}`} className={cn("flex items-center gap-2", isNativeAndroid && "native-v3-lineup-slot")}>
                  {(() => {
                    const draftChange = manualAwayDraftChanges[String(slot)];
                    return (
                      <>
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <div className="w-5 text-xs font-medium text-muted-foreground sm:text-sm">{slot}.</div>
                    <Select
                      value={String(manualAwayState[String(slot)] || "")}
                      onValueChange={(value) => handleManualLineupSelect("away", slot, value)}
                    >
                      <SelectTrigger className="h-8 w-full border-border bg-card px-3 text-sm">
                        {getManualLineupTriggerContent(
                          manualAwayState[String(slot)],
                          manualAwayOptionMap,
                          draftChange,
                          match?.away_team_id
                        )}
                      </SelectTrigger>
                      <SelectContent className={cn("z-[100] border-border bg-card text-card-foreground shadow-xl", isNativeAndroid && "native-v3-lineup-select")}>
                        {manualAwayOptions.map((rider) => {
                          const occupiedSlot = findManualLineupRiderSlot(manualAwayState, rider.id, slot);
                          const isSwapOption = Boolean(occupiedSlot);
                          return (
                            <SelectItem
                              key={`manual-away-option-${slot}-${rider.id}`}
                              value={String(rider.id)}
                              className={cn(
                                "bg-card",
                                isSwapOption && "text-amber-700 focus:text-amber-800 dark:text-amber-300 dark:focus:text-amber-200"
                              )}
                            >
                              <div className="flex w-full min-w-0 flex-nowrap items-center gap-3">
                                <span className="flex min-w-0 flex-1 flex-nowrap items-center gap-2 overflow-hidden whitespace-nowrap">
                                  <span className="truncate">{rider.name}</span>
                                  {isGuestRiderForTeam(rider, match?.away_team_id) ? <GuestRiderBadge className="shrink-0" /> : null}
                                </span>
                                {isSwapOption ? (
                                  <span className="ml-auto inline-flex shrink-0 items-center gap-1 text-[11px] font-medium">
                                    <Repeat2 className="h-3.5 w-3.5" />
                                    Byt med nr {occupiedSlot}
                                  </span>
                                ) : null}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                      </>
                    );
                  })()}
                </div>
              ))}
              </section>
            </div>
          </div>
          <DialogFooter className={cn("gap-2 border-t px-4 py-3 sm:px-5", isNativeAndroid && "native-v3-lineup-footer")}>
            <Button variant="ghost" type="button" onClick={requestCloseManualLineupModal}>
              Avbryt
            </Button>
            <Button
              type="button"
              onClick={handleSaveManualLineup}
              disabled={!manualLineupDirty || !manualLineupComplete || updateManualLineup.isPending}
            >
              {updateManualLineup.isPending ? "Sparar..." : "Spara laguppställning"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog
        open={Boolean(pendingManualSwap)}
        onOpenChange={(open) => {
          if (!open) setPendingManualSwap(null);
        }}
      >
        <AlertDialogContent className={isNativeAndroid ? "native-v3-protocol-alert" : undefined}>
          <AlertDialogHeader>
            <AlertDialogTitle>Byta plats i laguppställningen?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingManualSwap
                ? (
                  <>
                    Vill du byta plats på förare{" "}
                    <span className="font-semibold text-amber-700 dark:text-amber-300">
                      {pendingManualSwap.targetSlot}. {pendingManualSwap.selectedRiderName}
                    </span>{" "}
                    mot förare{" "}
                    <span className="font-semibold text-amber-700 dark:text-amber-300">
                      {pendingManualSwap.slot}. {pendingManualSwap.currentRiderName}
                    </span>
                    ?
                  </>
                )
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={confirmManualSwap}>Byt plats</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={pendingManualLineupDiscard}
        onOpenChange={(open) => {
          if (!open) setPendingManualLineupDiscard(false);
        }}
      >
        <AlertDialogContent className={isNativeAndroid ? "native-v3-protocol-alert" : undefined}>
          <AlertDialogHeader>
            <AlertDialogTitle>Avbryta ändringar?</AlertDialogTitle>
            <AlertDialogDescription>
              Du har osparade ändringar i laguppställningen. Om du stänger nu går ändringarna förlorade.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Fortsätt redigera</AlertDialogCancel>
            <AlertDialogAction onClick={closeManualLineupModal}>
              Stäng utan att spara
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={Boolean(pendingManualLineupReset)}
        onOpenChange={(open) => {
          if (!open) setPendingManualLineupReset(null);
        }}
      >
        <AlertDialogContent className={isNativeAndroid ? "native-v3-protocol-alert" : undefined}>
          <AlertDialogHeader>
            <AlertDialogTitle>Återställ laguppställning?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingManualLineupReset
                ? pendingManualLineupReset.hasProtocolProgress
                  ? (
                    <>
                      Detta återställer{" "}
                      <span className="font-semibold text-amber-700 dark:text-amber-300">
                        {pendingManualLineupReset.teamName}s laguppställning
                      </span>{" "}
                      till den ursprungliga laguppställningen för protokollet. Eftersom resultat har registrerats
                      kommer även heat och poäng att återställas för hela protokollet.
                    </>
                  )
                  : (
                    <>
                      Detta återställer{" "}
                      <span className="font-semibold text-amber-700 dark:text-amber-300">
                        {pendingManualLineupReset.teamName}s laguppställning
                      </span>{" "}
                      till den ursprungliga laguppställningen för protokollet.
                    </>
                  )
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={confirmManualLineupReset}>
              Återställ
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {lineupSync?.status === "outdated" ? (
        <Card
          ref={lineupAlertRef}
          className={cn(
            "border-amber-500/40 bg-amber-500/5 transition-all duration-500",
            !lineupSync?.can_refresh && "border-destructive/40 bg-destructive/5",
            lineupAlertHighlighted && "ring-2 ring-amber-400/80 ring-offset-2 ring-offset-background shadow-[0_0_0_1px_rgba(251,191,36,0.25),0_0_28px_rgba(251,191,36,0.2)]"
          )}
        >
          <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <AlertTriangle className="h-4 w-4" />
                {lineupSync?.can_refresh
                  ? "Laguppställningen har ändrats"
                  : "Protokollet bygger på äldre laguppställning"}
              </div>
              <p className="text-sm text-muted-foreground">
                {lineupSync?.can_refresh
                  ? "Den officiella laguppställningen har ändrats sedan du skapade protokollet. Eftersom inga heat har körts ännu kan du uppdatera protokollet till senaste lineup."
                  : "Den officiella laguppställningen har ändrats sedan du skapade protokollet. Protokollet har redan påbörjats. Du kan fortsätta som det är eller börja om med senaste lineup."}
              </p>
            </div>
            {lineupSync?.can_refresh ? (
              <Button onClick={handleRefreshProtocolLineup} disabled={refreshLineup.isPending}>
                {refreshLineup.isPending ? "Uppdaterar..." : "Uppdatera lineup"}
              </Button>
            ) : (
              <Button variant="destructive" onClick={handleRestartProtocolLineup} disabled={restartLineup.isPending}>
                {restartLineup.isPending ? "Börjar om..." : "Börja om protokoll"}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : null}
      {/* Match Header */}
      <Card id="matchProtocolOverview" className="native-v3-running-scoreboard bg-card border-secondary">
        <CardContent className="space-y-2 px-4 pb-3 pt-3 sm:px-5 sm:pb-4">
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 text-center sm:gap-4">
            <div className="flex flex-col items-center">
              <TeamLogo
                teamName={match.home_team}
                size={32}
                className="mb-1 h-8 w-8 shrink-0 object-contain"
              />
              <div className="mb-1 text-[11px] font-medium leading-tight text-gray-600 sm:text-xs">
                {match.home_team}
              </div>
              <div className="mt-1 flex justify-center space-x-1">
                <div className="h-3 w-3 rounded-full bg-red-600"></div>
                <div className="h-3 w-3 rounded-full bg-blue-600"></div>
              </div>
            </div>
            <div className="flex flex-col items-center justify-center gap-1 self-center">
              <div className="flex items-center justify-center gap-2">
                <div className="text-[28px] font-bold leading-none text-red-600 sm:text-[30px]">
                  {homeScore}
                </div>
                <div className="text-sm text-gray-400 sm:text-base">-</div>
                <div className="text-[28px] font-bold leading-none text-yellow-600 sm:text-[30px]">
                  {awayScore}
                </div>
              </div>
              <div className="flex items-center gap-3 text-[11px] text-muted-foreground sm:text-xs">
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                  <CalendarDays className="h-3.5 w-3.5" />
                  {formatMatchDate(match.date)}
                </span>
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                  <Clock3 className="h-3.5 w-3.5" />
                  {formatMatchTime(match.date)}
                </span>
              </div>
            </div>
            <div className="flex flex-col items-center">
              <TeamLogo
                teamName={match.away_team}
                size={32}
                className="mb-1 h-8 w-8 shrink-0 object-contain"
              />
              <div className="mb-1 text-[11px] font-medium leading-tight text-gray-600 sm:text-xs">
                {match.away_team}
              </div>
              <div className="mt-1 flex justify-center space-x-1">
                <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
                <div className="h-3 w-3 rounded-full border border-gray-300 bg-white"></div>
              </div>
            </div>
          </div>
          <div className="native-v3-running-statusbar flex items-center justify-between gap-3 text-[11px] text-muted-foreground sm:text-xs">
            <div className="native-v3-running-heat-status inline-flex items-center gap-1.5">
              <Flag className="h-3.5 w-3.5" />
              Heat {completedHeats}/15
            </div>
            <div className="native-v3-running-tools flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setManualHomeState(buildManualLineupState(match?.lineups?.home || []));
                  setManualAwayState(buildManualLineupState(match?.lineups?.away || []));
                  setManualLineupOpen(true);
                }}
                className="h-7 gap-1.5 px-2 text-[11px] sm:h-8 sm:text-xs"
              >
                <Users className="h-3.5 w-3.5" />
                Laguppställning
              </Button>
              {hasHeats ? (
                <Button
                  id="matchStatisticsButton"
                  variant="ghost"
                  onClick={() => setStatsOpen(true)}
                  className="h-auto gap-1.5 px-0 py-0 text-[11px] font-medium text-muted-foreground hover:bg-transparent hover:text-foreground sm:text-xs"
                >
                  <BarChart3 className="h-3.5 w-3.5" />
                  Matchstatistik
                </Button>
              ) : null}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Annons */}
        <AdSlot
          key={`protocol-top-${id}-${resumeToken}`}
          homeTeamId={homeTeamId}
          // awayTeamId={awayTeamId} // tillfälligt bortkommenterad - Detta visar annons för bortalaget också, vilket inte är önskvärt just nu
          placement="banner_protocol"
          slotKey="protocol-top"
        width={320}
        height={60}
          className="mx-auto"
          refreshMs={45000}
          minSwapMs={30000}
          timeDrivenRotation
          lazy={false}
        />

      {/* test  */}
      {/* Heat Program - Carousel */}
            <div id="matchHeatsCarousel" className="native-v3-running-heats space-y-3">
        <Card className="native-v3-running-heats-card min-w-0 overflow-hidden">
          <CardHeader className="px-4 pb-2 pt-4 sm:px-5">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-lg">
                Heatprogram
              </CardTitle>
              {protocolViewToggle}
            </div>
          </CardHeader>
          <CardContent className="min-w-0 px-4 pb-4 pt-0 sm:px-5">
            {protocolViewMode === "table" ? (
              <div id="matchProtocolTableView">
                <HeatProgramMatrix
                  heats={match?.heats}
                  lineups={match?.lineups}
                  sourceLineups={match?.source_lineups}
                  lineupSubstitutions={match?.lineup_substitutions}
                  showLineupChangeStatus={Boolean(match?.manual_lineup_updated_at)}
                  homeTeamName={match.home_team}
                  awayTeamName={match.away_team}
                  showResults
                  onHeatClick={handleOpenHeatFromTable}
                  onNominationClick={handleOpenNominationFromTable}
                  focusedHeatNumber={tableTargetHeatNumber}
                  focusedTargetType={tableTargetType}
                  scrollToHeatNumber={tableFocusTarget?.heatNumber}
                  scrollTargetType={tableFocusTarget?.type}
                  focusRequestKey={tableFocusTarget?.requestKey}
                  onFocusedHeatVisibilityChange={setTableIsFocusedHeatInView}
                  showNominationColumn
                  nominationCompleted={heat15HasNomination}
                  nominationAvailable={canNominateHeat15 || heat15HasNomination}
                />
              </div>
            ) : (
              <HeatsCarousel
                match={match}
                onEditHeat={(heat) => openDialogFor(heat)}
                isHeatComplete={isHeatSavedComplete}
                onNominateHeat={() => setNomOpen(true)}
                canNominateHeat15={canNominateHeat15}
                onIndexChange={setCurrentHeatIndex}
                onSlideChange={setCurrentSlideMeta}
                focusTarget={focusTarget}
                embedded
              />
            )}
          </CardContent>
        </Card>
      </div>



      {renderedPrimaryActionArea}


      <MatchStatsDialog
        open={statsOpen}
        onClose={() => setStatsOpen(false)}
        match={match}
        stats={matchStats}
      />

      <HeatDialog
        open={dialogOpen}
        onOpenChange={(o) => (o ? setDialogOpen(true) : closeDialog())}
        match={match}
        heat={selectedHeat}
        ridersByTeam={ridersByTeam}
        canUseTactical={canUseTactical}
        tacticalWindow={{
          start: tacticalRules(match).start,
          end: tacticalRules(match).end,
        }}
        heatSwapGuard={(liveHeat, draftRiders) => {
          let changed = 0;
          Object.keys(liveHeat?.riders || {}).forEach((g) => {
            const beforeId = String(liveHeat.riders[g]?.rider_id ?? "");
            const afterId = String(draftRiders[g]?.rider_id ?? "");
            if (beforeId !== afterId) changed++;
          });

          if (changed > 1) {
            toast.error("Endast en taktisk reserv per heat.", {
              description: "Återställ ett av bytena och försök igen.",
              duration: 4000,
            });
            return false; // stoppa spar
          }
          return true; // tillåt spar
        }}
        onSave={handleSave}
      />


      <NominationDialog
        open={nomOpen}
        onClose={() => setNomOpen(false)}
        match={match}
        onSaved={async () => {
          await refetch(); // hämta om matchen efter nomination-save
        }}
      />

      {/* Annons */}
        <AdSlot
          key={`protocol-bottom-${id}-${resumeToken}`}
          homeTeamId={homeTeamId}
          // awayTeamId={awayTeamId} // tillfälligt bortkommenterad - Detta visar annons för bortalaget också, vilket inte är önskvärt just nu
          placement="banner_protocol"
          slotKey="protocol-bottom"
        width={320}
        height={60}
          className="mx-auto"
          refreshMs={45000}
          minSwapMs={30000}
          timeDrivenRotation
          lazy={false}
        />
    </div>
  );
}



